import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApprovalDecision } from '../../types/chat';
import { useBizyChatActions } from '../context/actions-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { CheckOutline, XOutline } from '../lib/icons';
import { BizyRecognitionInline } from '../lib/recognition-inline';
import type { BizyNodeProps } from './node-props';

interface MetadataItem {
	key: string;
	value: string;
}

type ApprovalCardPayload = {
	variant?: string;
	title?: string;
	recipients?: string[];
	amount?: number;
	message?: string;
	hashtags?: string[];
	footer?: string;
	metadata?: MetadataItem[];
	confirmLabel?: string;
	confirmMessage?: string;
};

function ApprovalCardReceipt({ choice, receiptLabel }: { choice: ApprovalDecision; receiptLabel?: string }) {
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const isApproved = choice === 'approved';
	const label = receiptLabel ?? (isApproved ? t('approval_card.approved') : t('approval_card.denied'));

	return (
		<motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Flex align="center" gap={2} px={3} py={2.5} borderRadius={12} bg={isApproved ? 'success.bg' : 'bg.subtle'}>
				<Flex align="center" justify="center" boxSize={5} borderRadius="full" bg={isApproved ? 'success.icon' : 'secondary.bg'} flexShrink={0}>
					{isApproved ? <CheckOutline boxSize={3} color="text.onColor" /> : <XOutline boxSize={3} color="text.soft" />}
				</Flex>
				<Text fontSize="sm" fontWeight="medium" color={isApproved ? 'success.text' : 'text.soft'}>
					{label}
				</Text>
			</Flex>
		</motion.div>
	);
}

function ApprovalCardShell({
	title,
	children,
	metadata,
	footer,
	confirmLabel,
	isDisabled,
	onConfirm,
}: {
	title: string;
	children?: ReactNode;
	metadata?: MetadataItem[];
	footer?: string;
	confirmLabel?: string;
	isDisabled: boolean;
	onConfirm: () => void;
}) {
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const hasBody = !!children || (metadata && metadata.length > 0);

	return (
		<motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
			<Box border="1px solid" borderColor="border.default" borderRadius={12} bg="bg.surface" overflow="hidden">
				<Box px={3} py={2.5} borderBottom="1px solid" borderColor="border.default">
					<Text fontSize="sm" fontWeight="semibold" color="text.default">
						{title}
					</Text>
				</Box>

				{hasBody && (
					<Box px={3} py={3}>
						{children}
						{metadata && metadata.length > 0 && (
							<Flex direction="column" gap={1} mt={children ? 2 : 0}>
								{metadata.map((item) => (
									<Flex key={item.key} justify="space-between" align="baseline" gap={3}>
										<Text fontSize="xs" color="text.softer" flexShrink={0}>
											{item.key}
										</Text>
										<Text fontSize="xs" color="text.default" textAlign="right" noOfLines={2}>
											{item.value}
										</Text>
									</Flex>
								))}
							</Flex>
						)}
					</Box>
				)}

				<Flex align="center" justify="space-between" px={3} pb={3} pt={!hasBody ? 3 : 0}>
					<Text fontSize="xs" color="text.softest">
						{footer}
					</Text>
					<Button size="sm" onClick={onConfirm} isDisabled={isDisabled} flexShrink={0}>
						{confirmLabel ?? t('approval_card.approve')}
					</Button>
				</Flex>
			</Box>
		</motion.div>
	);
}

/**
 * Legacy `approval_card` node — an approve-to-send card (recognition or
 * generic). Records the choice through the host's `onChoiceUpdate` and sends
 * the confirm message through `onConfirm`.
 */
export function ApprovalCardNode({ node, cardIndex }: BizyNodeProps) {
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const p = (node as { payload: ApprovalCardPayload }).payload;
	const { messageKey, nodeChoices, onConfirm, onChoiceUpdate, isReadOnly } = useBizyChatActions();
	const externalChoice = nodeChoices?.[cardIndex];
	const [localChoice, setLocalChoice] = useState<ApprovalDecision | undefined>();
	const choice = externalChoice ?? localChoice;
	const isRec = p.variant === 'recognition';

	const handleConfirm = useCallback(() => {
		setLocalChoice('approved');
		if (messageKey && onChoiceUpdate) onChoiceUpdate(messageKey, cardIndex, 'approved');
		if (p.confirmMessage && onConfirm) onConfirm(cardIndex, p.confirmMessage);
	}, [cardIndex, messageKey, onChoiceUpdate, onConfirm, p.confirmMessage]);

	const receiptLabel =
		isRec && p.recipients?.length ? t('approval_card.recognition_sent', { recipients: p.recipients.map((r) => `@${r}`).join(', ') }) : undefined;

	if (choice) {
		return (
			<Box my={2}>
				<ApprovalCardReceipt choice={choice} receiptLabel={receiptLabel} />
			</Box>
		);
	}

	return (
		<Box my={2}>
			<ApprovalCardShell
				title={p.title ?? t('approval_card.default_title')}
				metadata={!isRec ? p.metadata : undefined}
				footer={p.footer}
				confirmLabel={p.confirmLabel}
				isDisabled={isReadOnly}
				onConfirm={handleConfirm}
			>
				{isRec && <BizyRecognitionInline recipients={p.recipients} amount={p.amount} message={p.message} hashtags={p.hashtags} />}
			</ApprovalCardShell>
		</Box>
	);
}
